import React, { useEffect, useState } from "react";
import { api, formatErr, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, StatusPill } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, Save, Clock } from "lucide-react";
import { toast } from "sonner";

const KEY_LABELS = {
  booking_confirmation: "Booking Confirmation",
  pickup_reminder: "Pickup Reminder (24h before)",
  return_reminder: "Return Reminder (24h before)",
  overdue: "Overdue Notice",
  payment_reminder: "Payment Reminder",
  missing_docs: "Missing Documents Reminder",
  toll_notice: "Toll / Ticket Notice",
  review_request: "Thank-you / Review Request",
};

const PLACEHOLDERS = [
  "customer_name",
  "vehicle_year_make_model",
  "pickup_date",
  "pickup_time",
  "return_date",
  "return_time",
  "balance_due",
  "total_cost",
  "business_name",
  "google_review_link",
  "plate",
  "total",
  "violation_date",
];

export default function SmsTemplates() {
  const [templates, setTemplates] = useState([]);
  const [log, setLog] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [send, setSend] = useState({ to: "", body: "" });

  const load = async () => {
    const [t, l] = await Promise.all([api.get("/sms/templates"), api.get("/sms/log")]);
    setTemplates(t.data);
    setLog(l.data);
  };
  useEffect(() => {
    load();
  }, []);

  const updateLocal = (key, patch) => setTemplates((arr) => arr.map((t) => (t.key === key ? { ...t, ...patch } : t)));

  const saveTpl = async (t) => {
    try {
      await api.put(`/sms/templates/${t.key}`, { key: t.key, body: t.body, enabled: t.enabled });
      toast.success(`Saved ${KEY_LABELS[t.key] || t.key}`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const sendManual = async () => {
    try {
      if (!send.to || !send.body) return toast.error("Both fields required");
      const r = await api.post("/sms/send", send);
      if (r.data?.status === "sent") toast.success("SMS sent");
      else if (r.data?.status === "skipped") toast.info("Salesmsg not configured (logged only)");
      else toast.error(r.data?.error || "Failed");
      setSendOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const refreshStatus = async () => {
    try {
      const r = await api.post("/sms/refresh-status");
      const n = r.data?.updated || 0;
      if (n > 0) toast.success(`Refreshed ${n} messages`);
      else toast.info("No pending messages to refresh");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="SMS Templates"
        subtitle="Automated messages"
        testId="sms-page-header"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={refreshStatus} data-testid="refresh-sms-status">
              <Clock size={14} className="mr-2" /> Refresh delivery
            </Button>
            <Button onClick={() => setSendOpen(true)} className="bg-black hover:bg-gray-800 text-white" data-testid="manual-sms-button">
              <Send size={14} className="mr-2" /> Send Manual SMS
            </Button>
          </div>
        }
      />

      <div className="mb-5 bg-blue-50 border border-blue-200 text-blue-900 rounded-md p-3 text-sm">
        Use <span className="font-mono">{"{placeholder}"}</span> tokens. Available: {PLACEHOLDERS.map((p) => <span key={p} className="font-mono">{`{${p}}`}</span>).reduce((a, b, i) => (a ? [a, ", ", b] : [b]), null)}.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {templates.map((t) => (
          <div key={t.key} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`tpl-card-${t.key}`}>
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <div className="font-heading font-semibold">{KEY_LABELS[t.key] || t.key}</div>
                <div className="text-xs text-gray-500 font-mono">{t.key}</div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Enabled</Label>
                <Switch
                  checked={t.enabled}
                  onCheckedChange={(v) => updateLocal(t.key, { enabled: v })}
                  data-testid={`tpl-enabled-${t.key}`}
                />
              </div>
            </div>
            <Textarea
              rows={4}
              value={t.body}
              onChange={(e) => updateLocal(t.key, { body: e.target.value })}
              className="text-sm"
              data-testid={`tpl-body-${t.key}`}
            />
            <div className="flex justify-end mt-2">
              <Button size="sm" onClick={() => saveTpl(t)} className="bg-black hover:bg-gray-800 text-white" data-testid={`tpl-save-${t.key}`}>
                <Save size={12} className="mr-1.5" /> Save
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <h2 className="font-heading text-xl font-semibold mb-3 flex items-center gap-2">
          <Clock size={18} /> Recent SMS Activity
        </h2>
        {log.length === 0 ? (
          <p className="text-sm text-gray-500">No messages sent yet.</p>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="sms-log-list">
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-3">When</th>
                    <th className="text-left px-4 py-3">To</th>
                    <th className="text-left px-4 py-3">Template</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Body</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {log.map((m) => (
                    <tr key={m.id}>
                      <td className="px-4 py-2 text-gray-600">{fmtDateTime(m.sent_at)}</td>
                      <td className="px-4 py-2 font-mono">{m.to || "—"}</td>
                      <td className="px-4 py-2">{m.template_key ? (KEY_LABELS[m.template_key] || m.template_key) : "Manual"}</td>
                      <td className="px-4 py-2"><StatusPill status={m.status === "sent" ? "Paid" : m.status === "failed" ? "Open" : "Billed"} /><span className="ml-2 text-xs">{m.status}</span></td>
                      <td className="px-4 py-2 text-gray-700 max-w-[400px] truncate">{m.body}{m.error && <span className="text-red-600 ml-2">{m.error}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent data-testid="manual-sms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Send Manual SMS</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>To (E.164)</Label>
              <Input data-testid="sms-to" value={send.to} onChange={(e) => setSend({ ...send, to: e.target.value })} placeholder="+15551234567" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea rows={4} data-testid="sms-body" value={send.body} onChange={(e) => setSend({ ...send, body: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} data-testid="sms-cancel">Cancel</Button>
            <Button onClick={sendManual} className="bg-black hover:bg-gray-800 text-white" data-testid="sms-send">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
